import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { sdkTypes } from '@vansite/ts-sharetribe-flex-sdk';

import Decimal from 'decimal.js';
import React, { memo, useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router';
import noImageSmall from '../../../assets/images/no-image-small.webp';
import noImage from '../../../assets/images/no-image.webp';
import config from '../../../config';
import routeConfiguration from '../../../routeConfiguration';
import { neutral, petrol, yellow } from '../../../styles/mui/colors';
import { formatMoney } from '../../../util/currency';
import { ensureListing } from '../../../util/data';
import usePass from '../../../util/hooks/usePass';
import usePriceData from '../../../util/hooks/usePriceData';
import { isCordova } from '../../../util/mobile/deviceDetection';
import { createResourceLocatorString } from '../../../util/routes';
import { createSlug } from '../../../util/urlHelpers';
import Link from '../../Links/Link/Link';
import Bookmark from '../Bookmark/Bookmark';
import ListingCardImage from './ListingCardImage';
import ReviewStars from './ListingCardReviewStar';
import ListingCardTagContainer from './ListingCardTagContainer';

const { Money, UUID } = sdkTypes;

export const styles = {
  card: {
    bgcolor: 'white',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
    borderRadius: '12px',
    overflow: 'hidden',
    willChange: 'transform',
    transition: 'opacity 0.2s ease-in-out',
    '@media (hover: hover)': {
      '&:hover': {
        cursor: 'pointer',
        opacity: 0.97,
      },
    },
  },
  cardMap: {
    display: 'flex',
    height: '100%',
    width: '300px',
    borderRadius: '8px',
    overflow: 'hidden',
    '@media (hover: hover)': {
      '&:hover': {
        cursor: 'pointer',
      },
    },
  },
  cardMedia: {
    overflow: 'hidden',
    position: 'relative',
    borderRadius: '8px',
    flex: 1,
  },
  cardContent: {
    bgcolor: 'white',
    '&:last-child': {
      paddingX: 1,
      paddingY: 1,
    },
  },
  cardContentInner: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  titleStyle: {
    fontWeight: '500',
    color: petrol.main,
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  priceStyle: {
    fontWeight: '500',
    color: petrol.main,
  },
  locationStyle: {
    color: neutral[500],
  },
  priceRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  savingsRow: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  savingsChip: {
    color: petrol.lightest,
    backgroundColor: yellow.lightest,
  },
};

const priceData = (price, intl) => {
  if (price && price.currency === config.currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency },
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency },
      ),
    };
  }
  return {};
};

const bookmarkWrapperStyle = { position: 'absolute', zIndex: 1, bottom: '10px', right: '10px' };
const listingCardWrapperStyle = { aspectRatio: '16 / 10' };
const wrapperStyles = {
  display: 'flex',
  flexDirection: 'column',
  color: 'white',
  textDecoration: 'none',
  '@media (hover: hover)': {
    '&:hover': { textDecoration: 'none', color: 'primary.main' },
  },
};

/**
 * ListingCardComponent renders a responsive card for a single listing, adjusting title length
 * based on screen size (mobile, tablet, desktop). It includes listing details like title, price,
 * ratings, and tags, with optional interactions for navigation and bookmarking.
 *
 * Props:
 * - listing: Listing data to display.
 * - setActiveListing: Callback for hover interactions.
 * - onListingInfoCardClicked: Callback for listing info click.
 * - disableLink: Disables navigation links.
 * - urlCategory: Optional category for URL generation.
 * @param root0
 * @param root0.addHelmet
 * @param root0.listing
 * @param root0.setActiveListing
 * @param root0.onListingInfoCardClicked
 * @param root0.createURLToListing
 * @param root0.disableBlur
 * @param root0.disableLink
 * @param root0.urlCategory
 */
const ListingCardComponent = ({
  addHelmet = true,
  listing,
  setActiveListing = (id) => null,
  onListingInfoCardClicked = null,
  createURLToListing = () => null,
  disableBlur = false,
  disableLink = false,
  urlCategory = null,
}) => {
  const navigate = useNavigate();
  const intl = useIntl();
  const [passIsValid, pass] = usePass();
  const isMobileViewport = isCordova() || (typeof window !== 'undefined' && window.innerWidth < 1200);

  const currentListing = useMemo(() => ensureListing(listing), [listing]);
  const { attributes, images } = currentListing;
  const { publicData, metadata, title, price, distance, createdAt } = attributes;
  const { category, verified, locationPlace = '' } = publicData;
  const isVerifiedListing = !!verified;

  const slug = useMemo(() => createSlug(`${locationPlace}-${title}`), [locationPlace, title]);
  const listingUrl = useMemo(
    () =>
      currentListing?.id?.uuid
        ? createResourceLocatorString(
          urlCategory ? 'ListingPageUrlCategory' : 'ListingPage',
          routeConfiguration(),
          {
            id: currentListing.id.uuid,
            slug,
            ...(urlCategory ? { category: urlCategory } : {}),
          },
          {},
          intl.locale,
        )
        : '',
    [currentListing, intl.locale, slug, urlCategory],
  );

  const handelLinkClick = useCallback(
    (e) => {
      e.preventDefault();
      if (e.target.tagName === 'svg' || e.target.tagName === 'path') return;
      if (isCordova() || window.innerWidth < 1200) {
        navigate(listingUrl);
      } else {
        window.open(listingUrl, '_blank');
      }
    },
    [navigate, listingUrl],
  );

  const listingHasSpecialPrice = metadata?.annualPassPrice;

  // Round commission price to nearest integer using Decimal.js
  const pricePlusCommission = new Decimal(!!listingHasSpecialPrice && passIsValid ? listingHasSpecialPrice.amount : price.amount)
    .times(1 + config.customerCommissionPercentage / 100)
    .round()
    .toNumber();

  const { formattedPrice, priceTitle } = priceData(
    new Money(pricePlusCommission, price.currency),
    intl,
  );

  // Price calculations
  const hasValidPrice = price && price.amount;
  const twentyFivePassAmount = hasValidPrice
    ? new Decimal(price.amount).times(0.25).round().toNumber()
    : 0;
  const halfPriceAmount = hasValidPrice
    ? new Decimal(!!listingHasSpecialPrice && passIsValid ? listingHasSpecialPrice.amount : price.amount).dividedBy(2).round().toNumber()
    : 0;
  const finalAmount = pass?.twentyFivePass ? new Decimal(price.amount).round().toNumber() : halfPriceAmount;
  const discountAmount = pass?.twentyFivePass ? twentyFivePassAmount : 0;

  const priceForCalculation = hasValidPrice
    ? new Money(finalAmount, config.currency)
    : new Money(0, config.currency);

  const { formattedPrice: reducedFormattedPrice, priceTitle: reducedPriceTitle } = usePriceData(
    priceForCalculation,
    discountAmount,
  );

  if (!currentListing?.id?.uuid) {
    return null;
  }

  const averageReview = metadata?.rating ? metadata?.rating / 100 : null;
  const reviewCount = metadata?.ratingCount ? metadata?.ratingCount : null;
  const firstImage = images[0] || {
    id: new UUID(),
    type: 'image',
    attributes: {
      variants: {
        'landscape-crop': { url: noImageSmall, width: 640, height: 400 },
        'landscape-crop2x': { url: noImage, width: 1900, height: 1000 },
      },
    },
  };

  const pricePrefix = intl.formatMessage({ id: 'ListingCard.prePriceTag' });
  const distanceToLocationLabel =
    distance && intl.formatMessage({ id: 'ListingCard.distanceTag' }, { distance });

  const listingIsAbovePriceThreshold = price.amount >= config.annualPass.lightPriceThreshold;
  const userHasPlusAnnualPass =
    pass &&
    !pass.twentyFivePass &&
    (pass.included === undefined ||
      pass.included?.plus === undefined ||
      pass.included?.plus === true);
  const showReducedPrice =
    passIsValid && listingIsAbovePriceThreshold ? !userHasPlusAnnualPass : null;
  const showTwentyFivePassPrice = passIsValid && pass.twentyFivePass;

  const listingCardPrice = !passIsValid ? (
    <Typography component="span" margin={0} sx={styles.priceStyle} title={priceTitle} variant="body2">
      {`${pricePrefix} ${formattedPrice}`}
    </Typography>
  ) : showReducedPrice || showTwentyFivePassPrice ? (
    <Typography component="span" margin={0} title={reducedPriceTitle} variant="body2">
      <Box component="span" sx={styles.priceStyle}>
        {`${pricePrefix}`} {`${reducedFormattedPrice}`}
      </Box>{' '}
      <Box
        component="span"
        sx={{
          textDecoration: 'line-through',
          color: 'text.disabled',
          fontSize: '0.875rem',
        }}
      >{`${formattedPrice}`}</Box>
    </Typography>
  ) : null;

  const priceAmount = !!listingHasSpecialPrice && passIsValid ? listingHasSpecialPrice.amount : listing.attributes.price.amount;
  const isPlus = priceAmount >= config.annualPass.lightPriceThreshold;

  const listingCardContent = (
    <>
      <Box sx={styles.cardMedia}>
        <ListingCardTagContainer
          category={category}
          createdAt={createdAt}
          isPlus={listingHasSpecialPrice ? false : isPlus}
          isVerifiedListing={isVerifiedListing}
        />
        <Box sx={bookmarkWrapperStyle}>
          <Bookmark listingId={listing.id} />
        </Box>
        <Box sx={listingCardWrapperStyle}>
          <ListingCardImage
            addHelmet={addHelmet}
            alt={title}
            disableBlur={disableBlur}
            image={firstImage}
            title={title}
          />
        </Box>
      </Box>
      <Box sx={styles.cardContent}>
        <Box sx={styles.cardContentInner}>
          {title ? (
            <Typography
              component="p"
              margin={0}
              sx={styles.titleStyle}
              title={title}
              variant="body2"
            >
              {title}
            </Typography>
          ) : (
            <Skeleton sx={{ fontSize: '1rem', lineHeight: 1 }} variant="text" width="50%" />
          )}
          <Typography component="p" margin={0} sx={styles.locationStyle} variant="body2">
            {`${distanceToLocationLabel ? distanceToLocationLabel + ' • ' : ''}${locationPlace}`}
          </Typography>
          <Typography component="p" margin={0} sx={styles.locationStyle} variant="body2">
            {category === 'expressBooking' ? (
              <FormattedMessage id="ListingCard.bookingCategory.expressBooking" />
            ) : (
              <FormattedMessage id="ListingCard.bookingCategory.bookingRequest" />
            )}
          </Typography>
          <Box sx={styles.priceRow}>
            <ReviewStars averageReview={averageReview} reviewCount={reviewCount} />
            {listingCardPrice}
          </Box>
          <Box sx={styles.savingsRow}>
            {!passIsValid ? (
              isPlus ? (
                <Chip
                  label={intl.formatMessage({ id: 'ListingCard.annualPassPlusSavings' }, { price: reducedFormattedPrice })}
                  size="small"
                  sx={styles.savingsChip} />
              ) : (
                <Chip label={intl.formatMessage({ id: 'ListingCard.annualPassSavings' })} size="small"
                      sx={styles.savingsChip} />
              )
            ) : null}
          </Box>
        </Box>
      </Box>
    </>
  );

  const content = !disableLink ? (
    <Link
      onClick={handelLinkClick}
      sx={wrapperStyles}
      target={isMobileViewport ? '_self' : '_blank'}
      to={listingUrl}
    >
      {listingCardContent}
    </Link>
  ) : (
    <Box sx={wrapperStyles}>{listingCardContent}</Box>
  );

  const listingCardComponent = (
    <Box
      onMouseEnter={() => setActiveListing(listing.id)}
      onMouseLeave={() => setActiveListing(null)}
      sx={styles.card}
    >
      {content}
    </Box>
  );

  const mapListingCardContent = (
    <Box
      onMouseEnter={() => setActiveListing(listing.id)}
      onMouseLeave={() => setActiveListing(null)}
      sx={styles.cardMap}
    >
      <a
        href={createURLToListing(currentListing)}
        onClick={(e) => {
          e.preventDefault();
          if (e.target.tagName === 'svg' || e.target.tagName === 'path') return;
          onListingInfoCardClicked(currentListing);
        }}
        rel="noreferrer"
        style={{
          textDecoration: 'none',
          color: 'inherit',
          width: '100%',
        }}
        target={isMobileViewport ? '_self' : '_blank'}
      >
        {listingCardContent}
      </a>
    </Box>
  );

  return !onListingInfoCardClicked ? listingCardComponent : mapListingCardContent;
};

ListingCardComponent.displayName = 'ListingCard';

const arePropsEqual = (prevProps, nextProps) => prevProps.listing.id.uuid !== nextProps.listing.id.uuid;

const ListingCard = memo(ListingCardComponent, arePropsEqual);

export default ListingCard;
